import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { IAdminSymbolDataset } from '../../api/admin';

interface Props {
	datasets: IAdminSymbolDataset[];
	loading: boolean;
	error: boolean;
	selectedDatasets: IAdminSymbolDataset[];
	helperText: string;
	onChange: (datasets: IAdminSymbolDataset[]) => void;
}

function formatDatasetTypes(dataset: IAdminSymbolDataset) {
	return dataset.ds_types.length > 0 ? ` / ${dataset.ds_types.join(', ')}` : '';
}

export default function AdminDatasetSelector({
	datasets,
	loading,
	error,
	selectedDatasets,
	helperText,
	onChange,
}: Props) {
	return (
		<Stack spacing={0.5}>
			<Autocomplete
				multiple
				disableCloseOnSelect
				options={datasets}
				loading={loading}
				value={selectedDatasets}
				onChange={(_, nextDatasets) => onChange(nextDatasets)}
				getOptionLabel={(dataset) => (
					`${dataset.vendor_name} / ${dataset.name}${formatDatasetTypes(dataset)} / ID ${dataset.id}`
				)}
				isOptionEqualToValue={(option, selected) => option.id === selected.id}
				limitTags={4}
				renderOption={(optionProps, option, { selected }) => (
					<li {...optionProps} key={option.id}>
						<Checkbox
							checked={selected}
							size="small"
							disableRipple
							tabIndex={-1}
							sx={{ mr: 1 }}
						/>
						<Box minWidth={0}>
							<Typography variant="body2" noWrap>
								{option.vendor_name} / {option.name}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								Dataset ID {option.id}
								{option.ds_types.length > 0 ? ` · Types: ${option.ds_types.join(', ')}` : ''}
							</Typography>
						</Box>
					</li>
				)}
				renderTags={(selected, getTagProps) => selected.map((dataset, index) => (
					<Chip
						label={`${dataset.vendor_name} / ${dataset.name} / ID ${dataset.id}`}
						size="small"
						{...getTagProps({ index })}
					/>
				))}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Datasets"
						placeholder={selectedDatasets.length === 0 ? 'Select datasets…' : ''}
						error={error}
						helperText={error
							? 'Datasets could not be loaded. Try again in a moment.'
							: helperText}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{loading ? <CircularProgress size={16} /> : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
				sx={{ minWidth: 0 }}
				fullWidth
			/>
		</Stack>
	);
}
